"use client";

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";

/** Same button treatment as the model-page mobile sticky CTA. */
export const MOBILE_STICKY_CTA_CLASS = "h-12 w-full rounded-xl bg-[#087F78] font-medium text-white";

type MobileStickyCtaProps = {
    anchorRef: RefObject<HTMLElement | null>;
    children: ReactNode;
    /** Mobile only: show on page load (news detail). Default: show after scrolling past anchor. */
    showOnLoad?: boolean;
    reserveSpace?: boolean; // Pads the page by the bar's height so it never covers the last content.
};

export default function MobileStickyCta({
    anchorRef,
    children,
    showOnLoad = false,
    reserveSpace = false,
}: MobileStickyCtaProps) {
    const [show, setShow] = useState(false);
    const barRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const bar = barRef.current;
        if (!show || !reserveSpace || !bar) return;
        const previous = document.body.style.paddingBottom;
        document.body.style.paddingBottom = `${bar.offsetHeight}px`;
        return () => { document.body.style.paddingBottom = previous; };
    }, [show, reserveSpace]);

    useEffect(() => {
        if (showOnLoad) {
            const sync = () => setShow(window.innerWidth < 768);
            sync();
            window.addEventListener("resize", sync);
            return () => window.removeEventListener("resize", sync);
        }

        const handleScroll = () => {
            if (window.innerWidth >= 768) {
                setShow(false);
                return;
            }
            const anchor = anchorRef.current;
            if (!anchor) return;
            const rect = anchor.getBoundingClientRect();
            setShow(rect.bottom < 120);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, [anchorRef, showOnLoad]);

    if (!show) return null;

    return (
        <div ref={barRef} className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white p-4 shadow-lg md:hidden">
            {children}
        </div>
    );
}
